import { createContext, useContext } from 'react';
import type { ConversationPageFacade, PrototyperBuildTimeline } from '@ideoz/core';
import type { SnapshotIndexEntry } from '../../prototyper/utils/shouldStartBuildStream';
import type { ResumeBuildOutcome } from '../../prototyper/utils/resumeBuildGating';

/**
 * Web auth-dialog state passed into the conversation UI. Shape mirrors
 * `useAuthDialog` (web-only).
 */
export interface ConversationAuthDialogState {
  isOpen: boolean;
  mode: 'login' | 'register';
  isLoading: boolean;
  openRegister: () => void;
  openLogin: () => void;
  closeDialog: () => void;
  switchMode: (mode: 'login' | 'register') => void;
  handleLogin: (email: string, password: string) => Promise<void>;
  handleRegister: (name: string, email: string, password: string) => Promise<void>;
  handleGoogleAuth: () => Promise<void>;
  handleNavigateToForgotPassword: () => void;
}

/**
 * All WebContainer / Prototyper workbench wiring for the conversation UI,
 * bundled into one object so it's a single context field rather than ~25 drilled
 * props. Built (and mode-gated) by `ConversationPage`. Phase 3 will swap this for
 * a lazily-mounted value so clarify never pays the workbench cost.
 */
export interface ConversationWorkbench {
  renderWorkbenchPanel?: (props: {
    isOpen: boolean;
    onClose: () => void;
    onToggleConversationPanel: () => void;
    isConversationPanelCollapsed: boolean;
    /** When true, workbench fills legacy prototyper panel (no duplicate toolbar). */
    embedded?: boolean;
    /** Merged into the Workbench toolbar so the prototyper has a single header. */
    headerLeftSlot?: React.ReactNode;
    headerRightSlot?: React.ReactNode;
    headerRightLeadSlot?: React.ReactNode;
  }) => React.ReactNode;
  workbenchToolActivity?: React.ReactNode;
  isStreamingPrototype?: boolean;
  streamingPublishReady?: boolean;
  /** Why Publish is disabled right now (build running, unsaved edits, preview loading…); null once ready. */
  streamingPublishDisabledReason?: string | null;
  streamingPreviewReady?: boolean;
  onStreamingPrototypeStart?: (ctx?: { llmInteractionId?: string }) => void;
  workbenchSnapshotBootstrap?: 'pending' | 'restored' | 'none' | 'failed';
  workbenchSnapshotRestoreError?: string | null;
  /** Bootstrap restored an earlier version because the latest file was missing. */
  workbenchSnapshotRestoreNotice?: string | null;
  /** A "Retry restore" is in progress; the pane shows a status banner. */
  workbenchSnapshotRestoreRetrying?: boolean;
  workbenchSnapshotIndexLoading?: boolean;
  getWorkbenchSnapshotIndexEntry?: (llmInteractionId: string) => SnapshotIndexEntry | undefined;
  onRetrySnapshotBootstrap?: () => void;
  activeSnapshotInteractionId?: string | null;
  /**
   * A publish (or unpublish) finished. The host records it on the turn that owns the prototype,
   * so the publish state survives a prototype switch without a page reload.
   */
  onPrototypePublished?: (result: { demoLinkId: number | null; slug: string | null }) => void;
  restoreInteractionLoadingId?: string | null;
  streamingWorkbenchLoading?: boolean;
  onStreamingWorkbenchPrototypePanelVisibilityChange?: (isOpen: boolean) => void;
  streamingWorkbenchLlmInteractionId?: string | null;
  livePrototyperBuildTimeline?: PrototyperBuildTimeline | null;
  isWorkbenchBuildLive?: boolean;
  workbenchTimelineTerminalVisible?: boolean;
  /** Resolves when the version is on screen; what it resolves to is the caller's business. */
  onPrototyperWorkbenchRestore?: (
    llmInteractionId: string,
    hint?: { buildStatus?: string; hasBuildTimeline?: boolean }
  ) => void | Promise<unknown>;
  uploadVerifiedPrototypeSnapshot?: () => Promise<string | undefined>;
  /** Locally finish a build interrupted by refresh/lost tab (no LLM stream). */
  resumeInterruptedBuild?: (
    llmInteractionId: string,
    options?: { userRequested?: boolean },
  ) => Promise<ResumeBuildOutcome>;
  onCancelWorkbenchStream?: () => void;
  /** The error banner over the prototype panel and what the founder can do about it. */
  workbenchPersistBanner?: import('../../prototyper/utils/workbenchBanner').WorkbenchBanner | null;
  onDismissWorkbenchPersistError?: () => void;
  /** The version waiting for the founder after automatic fixing stopped (P6.4b). */
  prototypeFixAttention?: import('../../prototyper/utils/autoFix').VersionAttention | null;
  /** Whether "Undo this change" has an earlier verified version to go back to. */
  prototypeFixCanUndo?: boolean;
  prototypeFixBusy?: 'retry' | 'undo' | 'rebuild' | null;
  /** Label for the live build card while an automatic fix runs. */
  workbenchAutoFixLabel?: string | null;
  onTryAnotherFix?: () => void;
  onUndoPrototypeChange?: () => void;
  loadPrototypeFixDetails?: () => Promise<string | null>;
  /**
   * Remove a build that did not finish from the prototype's versions (Task 723). Resolves false
   * when it could not be removed; the panel's banner then says why.
   */
  removeUnfinishedVersion?: (llmInteractionId: string) => Promise<boolean>;
  /**
   * "Build again" on a build that crashed or failed (Task 722): the turn's request is built once
   * more from the prototype's last finished version. Resolves false when it could not start.
   */
  buildAgain?: (llmInteractionId: string) => Promise<boolean>;
  /** The same, from the card of a build whose automatic fixes stopped. */
  onBuildAgainFromFix?: () => void;
  /** One version per turn from the server; auto-start decides from it (P6.4c). */
  turnVersions?: import('../../prototyper/utils/turnVersions').TurnVersionsLookup;
  /** Restore a failed turn's files and start its automatic fix (P6.4c). */
  resumeFailedVersion?: (llmInteractionId: string) => Promise<ResumeBuildOutcome>;
  /** Build an unfinished draft from the files the builder saved for its turn (P6.4d-3). */
  resumeDraftFromServerFiles?: (llmInteractionId: string) => Promise<ResumeBuildOutcome>;
  /**
   * "Fix with AI": rebuild the crashed version with the crash as its repair. False when it
   * cannot start; the caller then sends the chat message instead.
   */
  repairPreviewCrash?: (
    llmInteractionId: string,
    crash: import('@ideoz/workbench').WorkbenchPreviewCrash
  ) => boolean;
  /**
   * True when the founder opened this conversation from the artifacts library.
   * Opens the prototype panel without calling handleModeChange (that would
   * navigate to `…/new` on scope pages).
   */
  openPrototypePanelFromLibrary?: boolean;
}

/**
 * Everything the conversation UI needs, provided once by `ConversationPage`.
 * Replaces the 49-prop `ConversationModuleProps` drilling — descendants read
 * this via {@link useConversation} instead of receiving/re-passing props.
 */
export interface ConversationContextValue {
  facade: ConversationPageFacade;
  isWeb: boolean;
  urlConversationId?: string | null;
  authDialogState?: ConversationAuthDialogState;
  /** Scope back-navigation override (header back → Work Scopes). */
  onBack?: () => void;
  /** Scope 403/404 handler (re-resolve the scope instead of a full-screen 404). */
  onConversationNotFound?: () => void;
  /**
   * Artifact id from `?artifact=` when the founder opened this conversation
   * from the artifacts library. The conversation opens that document's panel.
   */
  openArtifactIdFromLibrary?: string | null;
  /** Owning turn from `?interaction=`, used to find that message in history. */
  openArtifactInteractionId?: string | null;
  workbench: ConversationWorkbench;
}

const ConversationContext = createContext<ConversationContextValue | null>(null);

export const ConversationProvider = ConversationContext.Provider;

/** Read the conversation context; throws if used outside `ConversationProvider`. */
export function useConversation(): ConversationContextValue {
  const ctx = useContext(ConversationContext);
  if (!ctx) {
    throw new Error('useConversation must be used within a ConversationProvider');
  }
  return ctx;
}
